
import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { PerkRating } from '@/lib/types';

interface PerkRatingFieldProps {
  perk: {
    id: string;
    text: string;
  };
  value: boolean | null;
  comment?: string;
  onChange: (perkId: string, perkText: string, isPositive: boolean | null, comment?: string) => void;
}

const PerkRatingField: React.FC<PerkRatingFieldProps> = ({
  perk,
  value,
  comment = '',
  onChange
}) => {
  const [showComment, setShowComment] = useState(!!comment || value !== null);
  
  const handleRating = (isPositive: boolean) => {
    // If the same button is clicked twice, toggle it off (set to null)
    const newValue = value === isPositive ? null : isPositive;
    onChange(perk.id, perk.text, newValue, comment);
    
    // Show comment field when a rating is selected
    if (newValue !== null && !showComment) {
      setShowComment(true);
    }
  };
  
  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(perk.id, perk.text, value, e.target.value);
  };

  return (
    <div className="flex flex-col py-2 border-b border-gray-100 last:border-b-0">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-800">{perk.text}</div>
        <div className="flex gap-2">
          <Button 
            type="button"
            variant="outline" 
            size="sm"
            className={`${value === true ? 'bg-green-50 border-green-200' : ''} p-2 h-8 w-8`}
            onClick={() => handleRating(true)}
          >
            <ThumbsUp className={`h-4 w-4 ${value === true ? 'text-green-600' : 'text-gray-400'}`} />
            <span className="sr-only">Good perk</span>
          </Button>
          
          <Button 
            type="button"
            variant="outline" 
            size="sm"
            className={`${value === false ? 'bg-red-50 border-red-200' : ''} p-2 h-8 w-8`}
            onClick={() => handleRating(false)}
          >
            <ThumbsDown className={`h-4 w-4 ${value === false ? 'text-red-600' : 'text-gray-400'}`} />
            <span className="sr-only">Bad perk</span>
          </Button>
        </div>
      </div>
      
      {showComment && (
        <div className="mt-2">
          <Textarea 
            placeholder="Add your comments about this perk..."
            className="w-full text-sm min-h-[60px]"
            value={comment || ''}
            onChange={handleCommentChange}
          />
        </div>
      )}
    </div>
  );
};

export default PerkRatingField;
